import { clear, el } from '../core/dom.js';
import { uuid } from '../core/ids.js';
import { track as gaTrack } from '../analytics/ga.js';
import { localDate, nowIso, tzOffsetMinutes } from '../core/time.js';
import { refresh } from '../core/state.js';
import { navigate } from '../router.js';
import {
  consumptionMethods, habits, logEvent, metaSet, plans, planStates,
  settings as settingsStore,
} from '../storage/db.js';
import {
  CATEGORY_TO_DOMAIN, DEFAULT_METHODS_BY_DOMAIN, buildMethodFromSeed,
  defaultMethodForLegacyHabit,
} from '../domain/default-methods.js';
import { DOMAIN_TEMPLATES, getTemplate } from '../domain/templates.js';
import type {
  BehaviorDomain, HabitCategory, HabitRecord, IntervalConfig, IntervalState,
  OnboardingMetadata, Pace, PlanRecord, PlanStateRecord, PlanType,
  QuotaConfig, QuotaState, UserSettings, WindowConfig, WindowState,
} from '../core/types.js';
import type { DomainTemplate, FirstWeekTarget, GoalStyle } from '../domain/templates.js';

const DRAFT_KEY = 'wean.quickStart.draft.v2';

interface QuickStartDraft {
  category?: HabitCategory | undefined;
  /** Picked method kinds (e.g. ['cigarette','vape']) for multi-seed domains. */
  methodKinds?: string[] | undefined;
  goalStyle?: GoalStyle | undefined;
  patternKey?: string | undefined;
  planType?: PlanType | undefined;
  pace?: Pace | undefined;
  /** When the user opts into calibration mode. */
  calibration?: boolean | undefined;
  rescuePreference?: 'distract' | 'calm' | 'reflect' | 'move' | 'surprise' | undefined;
  confidence?: 1 | 2 | 3 | 4 | 5 | undefined;
}

function loadDraft(): QuickStartDraft {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) as QuickStartDraft : {};
  } catch { return {}; }
}

function saveDraft(d: QuickStartDraft): void {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* ignore */ }
}

function clearDraft(): void { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } }

type StepFn = (host: HTMLElement, ctx: StepCtx) => void;
interface StepCtx { next: () => void; back: () => void; finish: () => Promise<void>; rerender: () => void; index: number; total: number }

let draft: QuickStartDraft = {};

export function renderQuickStart(parent: HTMLElement): void {
  draft = loadDraft();
  clear(parent);
  const wrap = el('div', { class: 'wizard' });
  const track = el('div', {
    class: 'progress-track',
    attrs: { role: 'progressbar', 'aria-valuemin': '1', 'aria-valuemax': '6' },
  });
  const fill = el('div', { class: 'progress-fill', attrs: { style: 'width:0%' } });
  track.appendChild(fill);
  const stepLabel = el('div', {
    class: 'progress-step-label',
    attrs: { 'aria-live': 'polite' },
  }, 'Step 1 of 6');
  const stage = el('div');
  wrap.appendChild(track);
  wrap.appendChild(stepLabel);
  wrap.appendChild(stage);
  parent.appendChild(wrap);

  const getSteps = (): StepFn[] => {
    const base: StepFn[] = [stepCategory];
    if (draft.category) {
      const domain = CATEGORY_TO_DOMAIN[draft.category];
      const seeds = DEFAULT_METHODS_BY_DOMAIN[domain];
      if (seeds && seeds.length > 1) base.push(stepMethods);
    }
    base.push(stepGoalStyle, stepPattern, stepFirstWeekTarget, stepRescuePreference, stepReview);
    return base;
  };
  let i = 0;
  const TRANSITION_CLASSES = ['step-in', 'step-in-forward', 'step-in-backward', 'step-out-forward', 'step-out-backward'];

  const mount = (direction: 'forward' | 'backward' | 'rerender' | 'initial'): void => {
    saveDraft(draft);
    clear(stage);
    const steps = getSteps();
    if (i >= steps.length) i = steps.length - 1;
    fill.setAttribute('style', `width:${Math.round(((i + 1) / steps.length) * 100)}%`);
    track.setAttribute('aria-valuenow', String(i + 1));
    track.setAttribute('aria-valuemax', String(steps.length));
    stepLabel.textContent = `Step ${i + 1} of ${steps.length}`;
    const ctx: StepCtx = {
      next: () => { i = Math.min(getSteps().length - 1, i + 1); renderStep('forward'); },
      back: () => { i = Math.max(0, i - 1); renderStep('backward'); },
      finish: async () => {
        await commit();
        await refresh();
        clearDraft();
        navigate('/today');
      },
      rerender: () => renderStep('rerender'),
      index: i, total: steps.length,
    };
    const step = steps[i];
    if (step) {
      gaTrack('onboarding_quick_step', { index: i, name: step.name || `step_${i}` });
      step(stage, ctx);
    }
    stage.classList.remove(...TRANSITION_CLASSES);
    void stage.offsetWidth;
    if (direction === 'forward') stage.classList.add('step-in-forward');
    else if (direction === 'backward') stage.classList.add('step-in-backward');
    else if (direction === 'initial') stage.classList.add('step-in');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  let transitioning = false;
  const OUT_DURATION_MS = 130;
  const renderStep = (direction: 'forward' | 'backward' | 'rerender' | 'initial' = 'initial'): void => {
    if (transitioning) return;
    if (direction === 'initial' || direction === 'rerender' || !stage.hasChildNodes()) {
      mount(direction);
      return;
    }
    transitioning = true;
    stage.classList.remove(...TRANSITION_CLASSES);
    void stage.offsetWidth;
    const outClass = direction === 'forward' ? 'step-out-forward' : 'step-out-backward';
    stage.classList.add(outClass);
    window.setTimeout(() => {
      transitioning = false;
      mount(direction);
    }, OUT_DURATION_MS);
  };
  renderStep('initial');
}

function actionsRow(ctx: StepCtx, opts: { primary: string; disabled?: boolean; onPrimary?: () => void; finish?: boolean }): HTMLElement {
  const row = el('div', { class: 'wizard-actions' });
  if (ctx.index > 0) {
    row.appendChild(el('button', { class: 'btn ghost', onclick: () => ctx.back() }, 'Back'));
  }
  row.appendChild(el('button', {
    class: 'btn primary', disabled: !!opts.disabled,
    onclick: async () => {
      opts.onPrimary?.();
      if (opts.finish) await ctx.finish();
      else ctx.next();
    },
  }, opts.primary));
  return row;
}

const stepCategory: StepFn = (host, ctx) => {
  host.appendChild(el('h1', {}, 'What are you working on?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, 'Pick the closest fit.'));
  const list = el('div', { class: 'choice-list' });
  for (const t of DOMAIN_TEMPLATES) {
    const c = el('button', {
      class: `choice ${draft.category === t.category ? 'selected' : ''}`,
      attrs: { type: 'button' },
      onclick: () => {
        const changed = draft.category !== t.category;
        draft.category = t.category;
        if (changed) {
          // Reset downstream picks only when category actually changes.
          draft.methodKinds = undefined;
          draft.goalStyle = undefined;
          draft.patternKey = undefined;
          draft.planType = undefined;
          draft.pace = undefined;
          draft.calibration = undefined;
          draft.confidence = undefined;
        }
        // Auto-advance — same pattern as goalStyle/pattern/rescue steps so the
        // first step doesn't flash from a re-render the user didn't request.
        ctx.next();
      },
    },
      el('div', {},
        el('div', {}, t.displayName),
        el('div', { class: 'text-muted', attrs: { style: 'font-size:0.8rem;' } }, t.examples.join(' · ')),
      ),
    );
    list.appendChild(c);
  }
  host.appendChild(list);
  host.appendChild(actionsRow(ctx, { primary: 'Continue', disabled: !draft.category }));
};

const stepMethods: StepFn = (host, ctx) => {
  const category = draft.category;
  if (!category) { ctx.back(); return; }
  const domain = CATEGORY_TO_DOMAIN[category];
  const seeds = DEFAULT_METHODS_BY_DOMAIN[domain];
  if (!seeds || seeds.length <= 1) { ctx.next(); return; }

  // Default: every kind selected. Preserves prior commit behaviour for users
  // who tap Continue without touching the list.
  if (draft.methodKinds === undefined) {
    draft.methodKinds = seeds.map((s) => s.kind);
  }
  const selected = new Set(draft.methodKinds);

  host.appendChild(el('h1', {}, 'Which kinds do you use?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, 'Pick any that apply. You can add or remove more later.'));

  const list = el('div', { class: 'choice-list' });
  for (const seed of seeds) {
    const checked = selected.has(seed.kind);
    list.appendChild(el('button', {
      class: `choice ${checked ? 'selected' : ''}`,
      attrs: { type: 'button' },
      onclick: () => {
        if (selected.has(seed.kind)) selected.delete(seed.kind);
        else selected.add(seed.kind);
        draft.methodKinds = [...selected];
        ctx.rerender();
      },
    },
      el('div', {},
        el('div', {}, seed.label),
        el('div', { class: 'text-muted', attrs: { style: 'font-size:0.8rem;' } }, seed.unitPlural),
      ),
    ));
  }
  host.appendChild(list);
  host.appendChild(actionsRow(ctx, {
    primary: 'Continue',
    disabled: selected.size === 0,
  }));
};

const stepGoalStyle: StepFn = (host, ctx) => {
  const template = currentTemplate();
  if (!template) { ctx.back(); return; }
  host.appendChild(el('h1', {}, 'How do you want to start?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, 'Pick the closest. You can change later.'));
  const list = el('div', { class: 'choice-list' });
  for (const opt of template.goalStyles) {
    const c = el('button', {
      class: `choice ${draft.goalStyle === opt.value ? 'selected' : ''}`,
      attrs: { type: 'button' },
      onclick: () => {
        draft.goalStyle = opt.value;
        draft.planType = opt.planType;
        ctx.next();
      },
    }, opt.label);
    list.appendChild(c);
  }
  host.appendChild(list);
  host.appendChild(actionsRow(ctx, { primary: 'Continue', disabled: !draft.goalStyle }));
};

const stepPattern: StepFn = (host, ctx) => {
  const template = currentTemplate();
  if (!template) { ctx.back(); return; }
  host.appendChild(el('h1', {}, 'What is your usual pattern?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, '"I\'m not sure" is fine — we\'ll start in calibration mode.'));
  const list = el('div', { class: 'choice-list' });
  for (const opt of template.patternOptions) {
    const c = el('button', {
      class: `choice ${draft.patternKey === opt.value ? 'selected' : ''}`,
      attrs: { type: 'button' },
      onclick: () => {
        draft.patternKey = opt.value;
        draft.calibration = opt.value === 'unsure';
        // Pattern can override plan type when the goal style is unsure.
        if (draft.goalStyle === 'unsure' && opt.suggestedPlanType) {
          draft.planType = opt.suggestedPlanType;
        }
        ctx.next();
      },
    }, opt.label);
    list.appendChild(c);
  }
  host.appendChild(list);
  host.appendChild(actionsRow(ctx, { primary: 'Continue', disabled: !draft.patternKey }));
};

const stepFirstWeekTarget: StepFn = (host, ctx) => {
  const template = currentTemplate();
  if (!template) { ctx.back(); return; }
  const planType = draft.planType ?? 'interval';
  const targets = template.firstWeekTargets[planType] ?? template.firstWeekTargets.interval;
  if (draft.calibration) {
    host.appendChild(el('h1', {}, 'Calibration week'));
    host.appendChild(el('p', { class: 'lead text-muted' },
      'For a few days, just log without judgment. The app will recommend a real target after it learns your pattern.'));
    host.appendChild(actionsRow(ctx, {
      primary: 'Continue with calibration',
      onPrimary: () => { draft.pace = 'gentle'; },
    }));
    return;
  }
  host.appendChild(el('h1', {}, 'Choose a first-week step'));
  const list = el('div', { class: 'choice-list' });
  for (const t of targets) {
    const c = el('button', {
      class: `choice ${draft.pace === t.pace ? 'selected' : ''}`,
      attrs: { type: 'button' },
      onclick: () => { draft.pace = t.pace; ctx.rerender(); },
    },
      el('div', {},
        el('div', {}, t.label),
        el('div', { class: 'text-muted', attrs: { style: 'font-size:0.85rem;' } }, t.description),
      ),
    );
    list.appendChild(c);
  }
  host.appendChild(list);
  host.appendChild(el('p', { class: 'muted', attrs: { style: 'margin-top:1rem;font-size:0.85rem;' } },
    'How doable does this feel? Wean uses this to bias adjustments.'));
  const conf = el('div', { class: 'kbd-list', attrs: { role: 'radiogroup', 'aria-label': 'Confidence' } });
  const confOpts: Array<{ v: 1 | 2 | 3 | 4 | 5; l: string }> = [
    { v: 1, l: 'Not at all' }, { v: 2, l: 'A little' }, { v: 3, l: 'Maybe' },
    { v: 4, l: 'Likely' }, { v: 5, l: 'Confident' },
  ];
  for (const o of confOpts) {
    const isActive = draft.confidence === o.v;
    const chip = el('button', {
      class: `tag${isActive ? ' active' : ''}`,
      attrs: { type: 'button', role: 'radio', 'aria-checked': String(isActive) },
      onclick: () => { draft.confidence = o.v; ctx.rerender(); },
    }, o.l);
    conf.appendChild(chip);
  }
  host.appendChild(conf);
  host.appendChild(actionsRow(ctx, { primary: 'Continue', disabled: !draft.pace }));
};

const stepRescuePreference: StepFn = (host, ctx) => {
  host.appendChild(el('h1', {}, 'When an urge hits, what usually helps?'));
  const opts: Array<{ value: NonNullable<QuickStartDraft['rescuePreference']>; label: string; types: string[] }> = [
    { value: 'distract', label: 'Distract me',                 types: ['minigame'] },
    { value: 'calm',     label: 'Calm my body',                types: ['breathing'] },
    { value: 'move',     label: 'Get me moving',               types: ['movement'] },
    { value: 'reflect',  label: 'Help me think clearly',       types: ['reflection'] },
    { value: 'surprise', label: 'Surprise me',                 types: ['minigame', 'breathing', 'movement', 'reflection'] },
  ];
  const list = el('div', { class: 'choice-list' });
  for (const o of opts) {
    list.appendChild(el('button', {
      class: `choice ${draft.rescuePreference === o.value ? 'selected' : ''}`,
      attrs: { type: 'button' },
      onclick: () => { draft.rescuePreference = o.value; ctx.next(); },
    }, o.label));
  }
  host.appendChild(list);
  host.appendChild(actionsRow(ctx, { primary: 'Continue', disabled: !draft.rescuePreference }));
};

const stepReview: StepFn = (host, ctx) => {
  const template = currentTemplate();
  if (!template) { ctx.back(); return; }
  host.appendChild(el('h1', {}, "You're ready"));
  const target = currentTarget(template);
  const lines: string[] = [
    `Working on: ${template.displayName}.`,
    draft.calibration
      ? 'First few days: calibration mode — log without judgment.'
      : `First-week step: ${target?.description ?? ''}`,
    'When an urge hits: tap "I\'m having an urge" to open urge support.',
    'Your data stays on this device.',
  ];
  if (template.safetyNote) lines.push(template.safetyNote);
  host.appendChild(el('div', { class: 'card' },
    ...lines.map((l) => el('div', { attrs: { style: 'margin-bottom:0.35rem;' } }, l)),
  ));
  const row = el('div', { class: 'wizard-actions' },
    el('button', { class: 'btn ghost', onclick: () => ctx.back() }, 'Adjust'),
    el('button', {
      class: 'btn primary',
      onclick: () => { void ctx.finish(); },
    }, 'Start my first week'),
  );
  host.appendChild(row);
};

function currentTemplate(): DomainTemplate | undefined {
  return draft.category ? getTemplate(draft.category) : undefined;
}

function currentTarget(template: DomainTemplate): FirstWeekTarget | undefined {
  const pt = draft.planType ?? 'interval';
  const list = template.firstWeekTargets[pt] ?? template.firstWeekTargets.interval;
  return list.find((t) => t.pace === draft.pace) ?? list[1] ?? list[0];
}

async function commit(): Promise<void> {
  const template = currentTemplate();
  if (!template) return;
  const category: HabitCategory = template.category;
  const domain: BehaviorDomain = CATEGORY_TO_DOMAIN[category] ?? 'other';
  const habitId = uuid();
  const planId = uuid();
  const [unitS, unitP] = template.defaultUnit;
  const habit: HabitRecord = {
    id: habitId,
    schemaVersion: 2,
    label: template.displayName,
    category,
    domain,
    unitSingular: unitS,
    unitPlural: unitP,
    active: true,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  await habits.put(habit);

  // Seed consumption methods. Multi-method domains (nicotine, alcohol) honour
  // the kinds the user picked in stepMethods; if none picked (single-method
  // domains, or skipped step) we fall back to every default seed.
  const seeds = DEFAULT_METHODS_BY_DOMAIN[domain];
  if (seeds && seeds.length > 0) {
    const picked = draft.methodKinds && draft.methodKinds.length > 0
      ? seeds.filter((s) => draft.methodKinds?.includes(s.kind))
      : seeds;
    const toSeed = picked.length > 0 ? picked : seeds;
    for (const seed of toSeed) {
      await consumptionMethods.put(buildMethodFromSeed(habit, domain, seed));
    }
  } else {
    await consumptionMethods.put(defaultMethodForLegacyHabit(habit));
  }

  const planType: PlanType = draft.planType ?? 'interval';
  const pace: Pace = draft.pace ?? 'steady';
  const target = currentTarget(template);
  const { config, state } = buildPlanConfig(planType, pace, target);
  const plan: PlanRecord = {
    id: planId, habitId, schemaVersion: 1, planType,
    pace,
    slipStyle: 'balanced',
    goalStyle: draft.goalStyle ? [draft.goalStyle] : [],
    config,
    active: true,
    origin: draft.calibration ? 'calibration' : 'quick_start',
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  await plans.put(plan);
  const ps: PlanStateRecord = { planId, habitId, schemaVersion: 1, state, updatedAt: nowIso() };
  await planStates.put(ps);

  const cur = await settingsStore.get();
  const rescueTypes = rescueTypesFor(draft.rescuePreference);
  const next: UserSettings = {
    ...cur,
    preferredRescueTypes: rescueTypes.length > 0 ? rescueTypes : cur.preferredRescueTypes,
  };
  await settingsStore.set(next);

  await metaSet('active_habit_id', habitId);
  await metaSet('active_plan_id', planId);
  await metaSet('onboarding_complete', true);
  const meta: OnboardingMetadata = {
    version: 2,
    quickStartCompletedAt: nowIso(),
    baselineConfidence: draft.calibration ? 'unknown' : 'estimated',
    selectedGoalStyle: draft.goalStyle ?? 'unsure',
    selectedPace: pace,
    templateCategory: category,
    tuneUpCardsSeen: [],
    tuneUpCardsCompleted: [],
    ...(draft.patternKey !== undefined ? { selectedPatternKey: draft.patternKey } : {}),
    ...(draft.confidence !== undefined ? { confidence: draft.confidence } : {}),
  };
  await metaSet('onboarding_metadata', meta);

  await logEvent({
    type: 'onboarding_completed', habitId, planId,
    occurredAt: nowIso(), localDate: localDate(), timezoneOffsetMinutes: tzOffsetMinutes(),
    payload: {
      planType, origin: plan.origin, goalStyle: draft.goalStyle,
      patternKey: draft.patternKey, pace, calibration: draft.calibration ?? false,
    },
  });
}

function rescueTypesFor(pref: QuickStartDraft['rescuePreference']): string[] {
  switch (pref) {
    case 'distract': return ['minigame'];
    case 'calm': return ['breathing'];
    case 'move': return ['movement'];
    case 'reflect': return ['reflection'];
    case 'surprise': return ['minigame', 'breathing', 'movement', 'reflection'];
    default: return ['minigame', 'breathing'];
  }
}

interface BuiltPlan {
  config: IntervalConfig | WindowConfig | QuotaConfig;
  state: IntervalState | WindowState | QuotaState;
}

function buildPlanConfig(planType: PlanType, _pace: Pace, target: FirstWeekTarget | undefined): BuiltPlan {
  if (planType === 'interval') {
    const startMin = target?.intervalMinutes ?? 5;
    const cfg: IntervalConfig = {
      baselineIntervalMs: Math.max(1, Math.round(startMin * 0.6)) * 60_000,
      startingIntervalMs: startMin * 60_000,
      minIntervalMs: 1 * 60_000,
      maxIntervalMs: 12 * 60 * 60_000,
    };
    return {
      config: cfg,
      state: {
        planType: 'interval',
        targetIntervalMs: cfg.startingIntervalMs,
        baselineIntervalMs: cfg.baselineIntervalMs,
        minIntervalMs: cfg.minIntervalMs,
        maxIntervalMs: cfg.maxIntervalMs,
        successStreak: 0, slipStreak: 0, momentum: 50,
      },
    };
  }
  if (planType === 'window') {
    const firstUse = target?.windowFirstUseMinutes ?? 12 * 60;
    const cfg: WindowConfig = {
      windowName: 'Daily',
      activeDays: [0, 1, 2, 3, 4, 5, 6],
      baselineFirstUseMinutes: Math.max(0, firstUse - 60),
      startingFirstUseMinutes: firstUse,
      shiftStepMinutes: 15,
    };
    return {
      config: cfg,
      state: {
        planType: 'window',
        windowName: cfg.windowName, activeDays: cfg.activeDays,
        baselineFirstUseMinutes: cfg.baselineFirstUseMinutes,
        targetFirstUseMinutes: cfg.startingFirstUseMinutes,
        shiftStepMinutes: cfg.shiftStepMinutes,
        successDays: 0, missDays: 0,
      },
    };
  }
  // quota / hybrid → use quota fallback
  const reduction = target?.quotaPercentReduction ?? 15;
  // Without a real baseline number, pick a small starter cap; user can tune later.
  const baseline = 14;
  const start = Math.max(1, Math.round(baseline * (1 - reduction / 100)));
  const cfg: QuotaConfig = { period: 'weekly', baselineQuota: baseline, startingQuota: start, reductionStep: 1 };
  const now = new Date();
  return {
    config: cfg,
    state: {
      planType: 'quota', period: 'weekly',
      baselineQuota: baseline, currentQuota: start,
      periodStartedAt: now.toISOString(),
      periodEndsAt: new Date(now.getTime() + 7 * 86400000).toISOString(),
      usedThisPeriod: 0, reductionStep: 1, successPeriods: 0,
    },
  };
}
